/**
 * Document Metadata Storage - Depersonalized metadata for locked content visibility
 *
 * Stores minimal, non-personal metadata about documents in plaintext IndexedDB.
 * This allows showing "3 notes (locked)" instead of empty state when content is locked.
 *
 * Privacy-First Design:
 * - Only stores counts, types, timestamps (no personal content)
 * - No titles, names, or identifiable information
 * - Metadata is optional (apps opt-in)
 */

export interface DocumentMetadata {
  documentId: string; // Unique document ID
  workspaceId: string; // Workspace this document belongs to
  appId: string; // App that owns this document (e.g., '@yada/app-docs')
  documentType: string; // Generic type (e.g., 'note', 'task', 'habit')
  itemCount: number; // Number of items in document (e.g., 3 notes, 5 tasks)
  createdAt: number; // Unix timestamp
  updatedAt: number; // Unix timestamp
}

export const DOCUMENT_METADATA_DB = 'yada-metadata';
const DB_NAME = DOCUMENT_METADATA_DB;
const DB_VERSION = 1;
const STORE_NAME = 'document_metadata';

let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Open or create metadata database
 */
function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      dbPromise = null;
      reject(request.error);
    };
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create metadata store
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'documentId' });

        // Indexes for querying
        store.createIndex('workspaceId', 'workspaceId', { unique: false });
        store.createIndex('appId', 'appId', { unique: false });
        store.createIndex('documentType', 'documentType', { unique: false });
        store.createIndex('workspace_app', ['workspaceId', 'appId'], { unique: false });
      }
    };
  });

  return dbPromise;
}

/**
 * Store or update document metadata
 */
export async function storeDocumentMetadata(metadata: DocumentMetadata): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    const request = store.put(metadata);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get metadata for a specific document
 */
export async function getDocumentMetadata(documentId: string): Promise<DocumentMetadata | null> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);

    const request = store.get(documentId);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all metadata for a workspace
 */
export async function getWorkspaceMetadata(workspaceId: string): Promise<DocumentMetadata[]> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('workspaceId');

    const request = index.getAll(workspaceId);

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get metadata summary for workspace + app
 */
export async function getMetadataSummary(
  workspaceId: string,
  appId: string
): Promise<{
  totalDocuments: number;
  totalItems: number;
  byType: Record<string, { count: number; items: number }>;
}> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('workspace_app');

    const request = index.getAll([workspaceId, appId]);

    request.onsuccess = () => {
      const docs: DocumentMetadata[] = request.result || [];

      const byType: Record<string, { count: number; items: number }> = {};
      let totalItems = 0;

      for (const doc of docs) {
        if (!byType[doc.documentType]) {
          byType[doc.documentType] = { count: 0, items: 0 };
        }
        byType[doc.documentType].count++;
        byType[doc.documentType].items += doc.itemCount;
        totalItems += doc.itemCount;
      }

      resolve({
        totalDocuments: docs.length,
        totalItems,
        byType,
      });
    };

    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete metadata for a document
 */
export async function deleteDocumentMetadata(documentId: string): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    const request = store.delete(documentId);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear all metadata for a workspace (e.g., when leaving workspace)
 */
export async function clearWorkspaceMetadata(workspaceId: string): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('workspaceId');

    const request = index.openCursor(workspaceId);

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest).result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        resolve();
      }
    };

    request.onerror = () => reject(request.error);
  });
}
